import { html } from 'lit';
import { JVElement } from '../../lib/base';
/**
 * Platform dashboard page. Matches the wireframe 02-dashboard.html.
 * Fetches from /api/operator/analytics, /api/operator/agents, /api/operator/audit, /api/operator/relay.
 */
export class JvOperatorDashboard extends JVElement {
    static { this.properties = {
        api: { type: Object },
        _analytics: { state: true },
        _agents: { state: true },
        _audit: { state: true },
        _relay: { state: true },
        _loading: { state: true },
        _error: { state: true },
    }; }
    constructor() {
        super();
        this._analytics = null;
        this._agents = [];
        this._audit = [];
        this._relay = [];
        this._loading = true;
        this._error = null;
    }
    connectedCallback() {
        super.connectedCallback();
        this._fetchAll();
    }
    async _fetchAll() {
        this._loading = true;
        this._error = null;
        try {
            const [analytics, agents, audit, relay] = await Promise.all([
                this.api.get('/api/operator/analytics').catch(() => null),
                this.api.get('/api/operator/agents').catch(() => []),
                this.api.get('/api/operator/audit?take=10').catch(() => []),
                this.api.get('/api/operator/relay').catch(() => []),
            ]);
            this._analytics = analytics;
            this._agents = agents;
            this._audit = audit;
            this._relay = relay;
        }
        catch (e) {
            this._error = e instanceof Error ? e.message : 'Failed to load dashboard data';
        }
        finally {
            this._loading = false;
        }
    }
    _formatNumber(n) {
        if (n >= 1000) {
            return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
        }
        return n.toLocaleString();
    }
    _formatCost(c) {
        return '$' + c.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    _timeAgo(dateStr) {
        if (!dateStr)
            return 'never';
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1)
            return 'just now';
        if (mins < 60)
            return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24)
            return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    }
    _formatTime(dateStr) {
        return new Date(dateStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    labelHealth(value) {
        return value || 'unknown';
    }
    _offlineAgents() {
        return this._agents.filter(a => !a.isOnline);
    }
    _renderAuditIcon(eventType) {
        const isStaff = eventType === 'assignment_created' || eventType === 'assignment_removed';
        const isQuota = eventType === 'quota_exceeded';
        if (isQuota) {
            return html `<span class="grid place-items-center w-9 h-9 rounded-xl bg-jv-warning-050 text-jv-warning shrink-0">
        <span class="jvi-clock text-[17px]"></span>
      </span>`;
        }
        if (isStaff) {
            return html `<span class="grid place-items-center w-9 h-9 rounded-xl bg-jv-accent-050 text-jv-accent-600 shrink-0">
        <span class="jvi-list text-[17px]"></span>
      </span>`;
        }
        return html `<span class="grid place-items-center w-9 h-9 rounded-xl bg-jv-primary-050 text-jv-primary shrink-0">
      <span class="jvi-eye text-[17px]"></span>
    </span>`;
    }
    _renderAuditBadge(eventType) {
        if (eventType === 'view_session_start') {
            return html `<jv-badge tone="live" label="Live"></jv-badge>`;
        }
        if (eventType === 'quota_exceeded') {
            return html `<jv-badge tone="warning" label="Quota" dot></jv-badge>`;
        }
        if (eventType === 'view_session_end') {
            return html `<jv-badge tone="neutral" label="Ended" dot></jv-badge>`;
        }
        return html `<jv-badge tone="online" label="Staff" dot></jv-badge>`;
    }
    render() {
        if (this._loading) {
            return html `<div class="max-w-content mx-auto px-5 sm:px-8 py-10">
        <p class="text-sm text-jv-ink-2">Loading platform overview…</p>
      </div>`;
        }
        if (this._error) {
            return html `<div class="max-w-content mx-auto px-5 sm:px-8 py-10">
        <div class="jv-card"><div class="jv-card-body text-jv-danger text-sm">${this._error}</div></div>
      </div>`;
        }
        const a = this._analytics;
        const offline = this._offlineAgents();
        return html `<div class="max-w-content mx-auto px-5 sm:px-8 py-7">
      <!-- Page header -->
      <div class="flex flex-wrap items-end gap-4 mb-6">
        <jv-page-head title="Platform overview" subtitle="Across all schools · month to date"></jv-page-head>
        <div class="ml-auto flex items-center gap-2">
          <span class="hidden sm:inline text-sm text-jv-ink-2 mr-1">Updated ${this._timeAgo(a?.generatedAt || null)}</span>
          <jv-button variant="ghost" size="sm" icon="gauge" label="Egress & cost" href="#09-analytics"></jv-button>
          <jv-button variant="primary" size="sm" icon="plus" label="Onboard school" href="#05-onboard"></jv-button>
        </div>
      </div>

      <!-- KPI row -->
      <div class="grid gap-5 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        <jv-stat-card label="Schools" .value=${a ? a.totalTenants.toLocaleString() : '—'} icon="building"></jv-stat-card>
        <jv-stat-card label="Live streams now" .value=${a ? this._formatNumber(a.activeCameras) : '—'} icon="activity" tone="accent"></jv-stat-card>
        <jv-stat-card label="Egress MTD" .value=${a ? this._formatCost(a.estimatedEgressCost) : '—'} icon="gauge" tone="success"></jv-stat-card>
        <jv-stat-card
          label="Agents online"
          .value=${a ? `${a.onlineAgents} / ${a.totalAgents}` : '—'}
          icon="server"
          tone=${offline.length > 0 ? 'warning' : 'default'}
          trend=${offline.length > 0 ? `${offline.length} offline` : ''}
        ></jv-stat-card>
      </div>

      <!-- Offline agents alert strip -->
      ${offline.length > 0
            ? html `<div class="flex flex-wrap items-center gap-3 rounded-2xl border border-jv-warning bg-jv-warning-050 px-4 py-3 mb-6">
            <span class="grid place-items-center w-9 h-9 rounded-xl bg-jv-warning text-white shrink-0">
              <span class="jvi-warning text-[18px]"></span>
            </span>
            <div class="text-sm min-w-0">
              <p class="font-semibold text-jv-ink">${offline.length} agent${offline.length !== 1 ? 's' : ''} reported offline by the agent service</p>
              <p class="text-jv-ink-2">Cameras at those sites may be unreachable. Worth a quick look before parents notice.</p>
            </div>
            <div class="ml-auto">
              <jv-button variant="ghost" size="sm" icon="activity" label="View agents" href="#08-agents"></jv-button>
            </div>
          </div>`
            : null}

      <!-- Two-column: activity + capacity -->
      <div class="grid gap-6 lg:grid-cols-2">
        <!-- Recent activity -->
        <div class="jv-card">
          <div class="jv-card-head">
            <span class="jvi-shield text-[18px] text-jv-primary"></span>
            <h3>Recent activity</h3>
            <a href="#10-audit" class="ml-auto text-xs font-semibold text-jv-primary hover:underline">Full audit log</a>
          </div>
          <div class="p-2">
            ${this._audit.length === 0
            ? html `<p class="text-sm text-jv-ink-2 px-3 py-4">No recent activity.</p>`
            : this._audit.map((ev) => html `<a href="#10-audit" class="flex items-center gap-3 p-3 rounded-xl hover:bg-jv-surface-2">
                    ${this._renderAuditIcon(ev.eventType)}
                    <span class="min-w-0 flex-1">
                      <b class="block text-sm text-jv-ink truncate">${ev.actorName} · ${ev.eventType.replace(/_/g, ' ')}</b>
                      <span class="text-xs text-jv-ink-2">${ev.tenantName}</span>
                    </span>
                    <span class="text-xs text-jv-ink-2 shrink-0 mr-1">${this._formatTime(ev.occurredAt)}</span>
                    ${this._renderAuditBadge(ev.eventType)}
                  </a>`)}
          </div>
        </div>

        <!-- Capacity by shard -->
        <div class="jv-card">
          <div class="jv-card-head">
            <span class="jvi-database text-[18px] text-jv-primary"></span>
            <h3>Capacity by shard</h3>
            <a href="#07-relay" class="ml-auto text-xs font-semibold text-jv-primary hover:underline">Relay fleet</a>
          </div>
          <div class="jv-card-body space-y-5">
            <p class="text-sm text-jv-ink-2 -mt-1">Relays are sharded by tenant. Live streams route to the shard that owns each school.</p>

            ${this._relay.length === 0
            ? html `<p class="text-sm text-jv-ink-2">No relay data available.</p>`
            : this._relay.map((r, i) => {
                const color = r.health.toLowerCase() === 'healthy' ? 'bg-jv-success' : 'bg-jv-warning';
                return html `<div>
                    <div class="flex items-center justify-between text-sm mb-1.5">
                      <span class="font-semibold text-jv-ink flex items-center gap-2">
                        <span class="jvi-server text-[15px] text-jv-ink-2"></span> shard-0${i + 1}
                      </span>
                      <span class="text-jv-ink-2"><b class="text-jv-ink">${r.activeStreams}</b> active streams · ${this.labelHealth(r.health)}</span>
                    </div>
                    <div class="flex items-center gap-2 text-xs text-jv-ink-2">
                      <span class="inline-block w-2 h-2 rounded-full ${color}"></span>
                      <span>${this.labelHealth(r.health)}</span>
                    </div>
                  </div>`;
            })}

            ${this._relay.some(r => r.activeStreams > 9)
            ? html `<div class="flex items-center gap-2 rounded-xl bg-jv-primary-050 px-3.5 py-3 text-sm text-jv-ink-2">
                  <span class="jvi-info text-[16px] text-jv-primary shrink-0"></span>
                  <span>A shard is nearing capacity. New schools are being routed to the least-loaded shard.</span>
                </div>`
            : null}
          </div>
        </div>
      </div>
    </div>`;
    }
}
